/* ==========================================================================
   EcomHub Pro — header auto-contrast
   Works out what is actually rendered BEHIND the header and flips the header
   content between light and dark so it stays readable.

   Why it samples the way it does:
   - it reads the point stack at the header's own mid-height (not below it), so
     it measures what the header is overlapping right now
   - it composites every layer bottom-to-top (scheme colours, overlays, and the
     real pixels of <img> / background-image layers), because an image banner
     has no background-colour of its own
   - if a frame can't be decided it KEEPS the last result instead of clearing,
     so the header never flickers back mid-scroll
   ========================================================================== */
(function () {
  var SAMPLE_SIZE = 32;
  var imgCache = Object.create(null); // src -> {w,h,data} | null (failed)
  var queued = false;
  var lastState = null;

  function parseRGBA(str) {
    var m = /rgba?\(([^)]+)\)/.exec(str || '');
    if (!m) return null;
    var p = m[1].split(',');
    var a = p.length > 3 ? parseFloat(p[3]) : 1;
    if (!(a > 0)) return null;
    return { r: parseFloat(p[0]), g: parseFloat(p[1]), b: parseFloat(p[2]), a: a };
  }

  function blend(under, over) {
    var a = over.a;
    return {
      r: over.r * a + under.r * (1 - a),
      g: over.g * a + under.g * (1 - a),
      b: over.b * a + under.b * (1 - a)
    };
  }

  /* Rasterise an image once at a small size so we can read its real colours.
     A fresh CORS-enabled Image is used because theme <img> tags usually lack a
     crossorigin attribute, which would taint the canvas. */
  function loadImage(src) {
    if (src in imgCache) return imgCache[src];
    imgCache[src] = null; // mark in-flight so we only try once
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var cv = document.createElement('canvas');
        cv.width = SAMPLE_SIZE;
        cv.height = SAMPLE_SIZE;
        var ctx = cv.getContext('2d');
        ctx.drawImage(probe, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        imgCache[src] = {
          w: SAMPLE_SIZE,
          h: SAMPLE_SIZE,
          data: ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
        };
        queue();
      } catch (e) {
        imgCache[src] = null; // cross-origin without CORS — give up quietly
      }
    };
    probe.src = src;
    return null;
  }

  /* average colour of the image row sitting at vertical position v (0..1) */
  function rowColor(cache, v) {
    var y = Math.min(cache.h - 1, Math.max(0, Math.round(v * (cache.h - 1))));
    var r = 0, g = 0, b = 0, n = 0;
    for (var x = 0; x < cache.w; x++) {
      var i = (y * cache.w + x) * 4;
      var a = cache.data[i + 3] / 255;
      if (a < 0.5) continue;
      r += cache.data[i];
      g += cache.data[i + 1];
      b += cache.data[i + 2];
      n++;
    }
    if (!n) return null;
    return { r: r / n, g: g / n, b: b / n, a: 1 };
  }

  function imageLayer(el, cs, y) {
    var src = null;
    if (el.tagName === 'IMG') src = el.currentSrc || el.src;
    else {
      var bi = cs.backgroundImage;
      if (bi && bi.indexOf('url(') !== -1) {
        var m = /url\(["']?([^"')]+)["']?\)/.exec(bi);
        if (m) src = m[1];
      }
    }
    if (!src) return null;
    var cache = imgCache[src] !== undefined ? imgCache[src] : loadImage(src);
    if (!cache) return null;
    var rect = el.getBoundingClientRect();
    var v = rect.height ? (y - rect.top) / rect.height : 0.5;
    return rowColor(cache, Math.min(1, Math.max(0, v)));
  }

  /* composite everything painted at this point, bottom layer first */
  function colorAt(x, y, wrap) {
    var stack;
    try {
      stack = document.elementsFromPoint(x, y);
    } catch (e) {
      return null;
    }
    if (!stack || !stack.length) return null;

    var acc = { r: 255, g: 255, b: 255 };
    var found = false;

    for (var i = stack.length - 1; i >= 0; i--) {
      var el = stack[i];
      if (!el || wrap.contains(el) || el === document.documentElement) continue;
      var cs = getComputedStyle(el);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;

      var bg = parseRGBA(cs.backgroundColor);
      if (bg) { acc = blend(acc, bg); found = true; }

      var img = imageLayer(el, cs, y);
      if (img) { acc = blend(acc, img); found = true; }

      /* overlays are often ::before/::after (and are invisible to
         elementsFromPoint when they use pointer-events:none) */
      ['::before', '::after'].forEach(function (pseudo) {
        var ps = getComputedStyle(el, pseudo);
        if (!ps || ps.content === 'none') return;
        var pbg = parseRGBA(ps.backgroundColor);
        if (pbg) { acc = blend(acc, pbg); found = true; }
      });
    }
    return found ? acc : null;
  }

  function sample() {
    queued = false;
    var h = document.querySelector('.header');
    var wrap = document.querySelector('.header-wrapper');
    if (!h || !wrap) return;

    function clear() {
      wrap.classList.remove('ehp-on-dark', 'ehp-on-light');
      lastState = null;
    }

    if (h.getAttribute('data-ehp-autocontrast') !== '1') return clear();

    var body = document.body;
    var scrolledGlass =
      wrap.classList.contains('ehp-scrolled') && h.getAttribute('data-ehp-scroll-style') === 'glass';

    /* only meaningful while something is genuinely showing through the header:
       floating over a hero, a glass header content scrolls under, or the
       scrolled-glass state. A plain in-flow header paints its own scheme
       background, so leave Dawn's colours alone. */
    if (
      !body.classList.contains('ehp-header-float') &&
      !body.classList.contains('ehp-glass') &&
      !scrolledGlass
    ) {
      return clear();
    }

    /* A header that is MEANT to float but is not actually over anything — the
       pull-up has not applied, or the first section is shorter than the header —
       has no backdrop to sample. This engine HOLDS its last result when a sample
       is undecidable, which in that state means it keeps an on-dark reading from
       when it WAS over the hero, and the logo, menu and search stay white on a
       white page. Clear instead, so Dawn's own scheme colours come back. */
    if (
      body.classList.contains('ehp-header-float') &&
      !body.classList.contains('ehp-float-ok') &&
      !scrolledGlass
    ) {
      return clear();
    }

    /* once a solid background has taken over there is nothing showing through */
    var solidScrolled =
      wrap.classList.contains('ehp-scrolled') &&
      h.getAttribute('data-ehp-solid') === '1' &&
      h.getAttribute('data-ehp-scroll-style') !== 'glass';
    if (solidScrolled) return clear();

    var r = wrap.getBoundingClientRect();
    if (r.height <= 0 || r.bottom < 0) return;
    var y = Math.round(r.top + r.height / 2);
    if (y < 0 || y >= window.innerHeight) return;

    var w = window.innerWidth;
    var xs = [Math.round(w * 0.2), Math.round(w * 0.5), Math.round(w * 0.8)];
    var lum = 0, n = 0;
    for (var i = 0; i < xs.length; i++) {
      var c = colorAt(xs[i], y, wrap);
      if (!c) continue;
      lum += (c.r * 299 + c.g * 587 + c.b * 114) / 1000;
      n++;
    }
    if (!n) return; // undecidable this frame — keep whatever we had

    var dark = lum / n < 128;
    var state = dark ? 'dark' : 'light';
    if (state === lastState) return;
    lastState = state;
    wrap.classList.toggle('ehp-on-dark', dark);
    wrap.classList.toggle('ehp-on-light', !dark);
  }

  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(sample);
  }

  function boot() {
    lastState = null;
    queue();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue, { passive: true });
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
})();
